import React, { useState, useEffect } from "react";
import logo from "../assets/img/logo.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { toast } from "sonner";
import { confirmRegistration } from "../api/auth";
import { PATH_AUTH } from "../routes/paths";
import Email from "../assets/icons/Email";

const Confirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  
  // Get the email from location state or localStorage
  const [email] = useState(() => {
    // Try to get from location state first
    if (location.state && location.state.email) {
      return location.state.email;
    }
    // Fall back to localStorage
    return localStorage.getItem('pendingVerificationEmail') || '';
  });
  
  // If no email is found, redirect to signup
  useEffect(() => {
    if (!email) {
      toast.error("Please complete signup first");
      navigate(PATH_AUTH.signup);
    }
  }, [email, navigate]);
  
  const handleCodeChange = (e) => {
    setVerificationCode(e.target.value);
  };
  
  const handleResendCode = async () => {
    if (!email) {
      setErrorMessage("Email address is missing. Please go back to signup.");
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await confirmRegistration(email, '');
      toast.success(response.data.message || "Verification code sent successfully");
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      toast.error(error.response?.data?.message || "Failed to resend verification code");
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!verificationCode) {
      setErrorMessage("Verification code is required");
      return;
    }
    
    if (!email) {
      setErrorMessage("Email address is missing. Please go back to signup.");
      return;
    }
    
    try {
      setIsLoading(true);
      const success = await confirmRegistration(email, verificationCode);
      
      if (success) {
        // Show success message
        toast.success("Account verified successfully");
        
        // Store the email for onboarding
        localStorage.setItem('onboardingEmail', email);
        
        // Clean up
        localStorage.removeItem('pendingVerificationEmail');
        
        // Redirect to onboarding page
        navigate(PATH_AUTH.onboarding);
      } else {
        throw new Error("Verification failed");
      }
    } catch (error) {
      // Handle specific Cognito error messages
      let errorMessage = "Invalid verification code";
      
      if (error.message && error.message.includes("ExpiredCodeException")) {
        errorMessage = "Verification code has expired. Please request a new one.";
      } else if (error.message && error.message.includes("CodeMismatchException")) {
        errorMessage = "Incorrect verification code. Please try again.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrorMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <main className="equaliq">
      <div className="login-page">
        <header>
          <div className="box">
            <div className="header-content">
              <Link to="/">
                <img src={logo} alt="Equal IQ Logo" />
              </Link>
            </div>
          </div>
        </header>
        
        <section className="login-section">
          <div className="box">
            <div className="confirmation-content">
              <div className="confirmation-left">
                <div className="confirmation-info">
                  <h4>Check Your Email</h4>
                  <p>We've sent a verification code to your email address. Please enter the code to continue.</p>
                </div>
              </div>
              
              <div className="confirmation-right">
                <h4>Verify Your Email <span>Step 2: Confirm your address</span></h4>
                
                <form className="form-detail" onSubmit={handleSubmit}>
                  <div className="email-display">
                    <Email />
                    <span>{email}</span>
                  </div>
                  
                  <div className="verification-instructions">
                    <p>We've sent a 6-digit verification code to your email address. This code will expire after 24 hours.</p>
                  </div>
                  
                  <div className="form-input-label">
                    <h5>Verification Code</h5>
                    <div className="form-label">
                      <input
                        type="text"
                        name="verificationCode"
                        id="verificationCode"
                        placeholder="Enter verification code"
                        value={verificationCode}
                        onChange={handleCodeChange}
                      />
                    </div>
                    {errorMessage && <span className="error">{errorMessage}</span>}
                  </div>
                  
                  <button type="submit" disabled={isLoading} className="verify-button">
                    {isLoading ? <Spinner variant="light" /> : "Verify Account"}
                  </button>
                  
                  <div className="confirmation-options">
                    <p>
                      Didn't receive a code? <button 
                        type="button" 
                        className="resend-link"
                        onClick={handleResendCode} 
                        disabled={isLoading}
                      >
                        Resend code
                      </button>
                    </p>
                    <p>
                      <Link to={PATH_AUTH.signup}>Go back to signup</Link>
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Confirmation;